import { useCallback, useEffect, useRef } from 'react'
import { getUserFiles } from '@/uppy-file-upload/fetch/get-user-files'
import { useAppDispatch } from '@/store/store-hooks'
import { UserFilesPollingActions } from '@/user_files/polling/store/slice'

export const kOcrQuotaWaiterRefreshIntervalMs = 60_000

type UseOcrQuotaWaiterRefreshOptions = {
  fileIds: string[]
  refreshQuota: () => Promise<void>
}

/**
 * Refresh durable quota waiters at the recovery cadence, outside rapid processing polling.
 * Returns a function that runs the same refresh now, for when capacity may have been released;
 * with nothing waiting it refreshes only the quota.
 */
export function useOcrQuotaWaiterRefresh({ fileIds, refreshQuota }: UseOcrQuotaWaiterRefreshOptions): () => Promise<void> {
  const dispatch = useAppDispatch()
  const normalizedFileIds = [...new Set(fileIds)].sort()
  const fileIdsKey = JSON.stringify(normalizedFileIds)
  const fileIdsRef = useRef(normalizedFileIds)
  const refreshQuotaRef = useRef(refreshQuota)
  const generationRef = useRef(0)
  const refreshNowRef = useRef(() => refreshQuotaRef.current())

  fileIdsRef.current = normalizedFileIds
  refreshQuotaRef.current = refreshQuota

  useEffect(() => {
    let active = true
    const generation = generationRef.current + 1
    generationRef.current = generation
    let timeoutId: number | undefined
    let inFlightRefresh: Promise<void> | null = null

    const isCurrent = () => active && generationRef.current === generation

    const clearScheduledRefresh = () => {
      if (timeoutId === undefined) return
      window.clearTimeout(timeoutId)
      timeoutId = undefined
    }

    const refreshWaitingState = (): Promise<void> => {
      if (inFlightRefresh) return inFlightRefresh

      const requestedIds = fileIdsRef.current
      inFlightRefresh = (async () => {
        try {
          await refreshQuotaRef.current()
          if (!isCurrent()) return

          const response = await getUserFiles(requestedIds, true)
          if (!isCurrent()) return

          dispatch(
            UserFilesPollingActions.pollBatchReceived({
              requestedIds,
              files: response.files,
            })
          )
        } catch (error) {
          if (isCurrent()) console.error('[OcrQuota] Error refreshing waiting state:', error)
        }
      })().finally(() => {
        if (isCurrent()) inFlightRefresh = null
      })

      return inFlightRefresh
    }

    const schedule = () => {
      clearScheduledRefresh()
      if (!isCurrent() || fileIdsRef.current.length === 0 || document.visibilityState === 'hidden') return

      timeoutId = window.setTimeout(() => {
        timeoutId = undefined
        void refreshWaitingState().finally(schedule)
      }, kOcrQuotaWaiterRefreshIntervalMs)
    }

    const handleVisibilityChange = () => {
      clearScheduledRefresh()
      if (!isCurrent() || document.visibilityState === 'hidden') return
      void refreshWaitingState().finally(schedule)
    }

    if (fileIdsRef.current.length === 0) return () => undefined

    refreshNowRef.current = () => {
      clearScheduledRefresh()
      return refreshWaitingState().finally(schedule)
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    schedule()
    return () => {
      active = false
      generationRef.current += 1
      clearScheduledRefresh()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      refreshNowRef.current = () => refreshQuotaRef.current()
    }
  }, [dispatch, fileIdsKey])

  return useCallback(() => refreshNowRef.current(), [])
}
